import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Pill, Suma } from '@/ui/primitives'
import { DataTable, type Coloana, type Sortare } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'
import { pereche, sursaCurs, type ContextCurs, type ModCurs } from '@/lib/money'
import { citesteMod, CursSelector } from '@/ui/curs-selector'
import { ASTEPTAM, CULOARE_ETAPA, type Etapa } from '@/lib/pipeline'

type Rezervare = {
  id: number; number: string; partner: string | null; booking_customer: string | null
  status: string | null; begin_date: string | null; end_date: string | null
  stage: string | null; waiting_on: string | null
  destination: string | null; main_destination: string | null; service: string | null
  responsible: string | null; total_value: number | null; currency: string | null
}
type Lista = { reservations: Rezervare[]; total: number; offset: number; limit: number }

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' }) : '—'

function stare(s: string | null) {
  const v = (s || '').toLowerCase()
  if (v.includes('anulat')) return <Badge tone="bad">Anulata</Badge>
  if (v.includes('confirm')) return <Badge tone="good">{s}</Badge>
  if (v.includes('optiune') || v.includes('opțiune')) return <Badge tone="warn">{s}</Badge>
  return <Badge tone="neutral">{s || '—'}</Badge>
}

export function Reservations() {
  const navigheaza = useNavigate()
  const qc = useQueryClient()
  const [cauta, setCauta] = useSearchParams()
  const statusUrl = cauta.get('status')
  const etapaUrl = cauta.get('etapa')
  const asteptamUrl = cauta.get('asteptam')

  const pipeline = useQuery({
    queryKey: ['pipeline-etape'],
    queryFn: () => api.get<{ etape: Etapa[]; total: number }>('/pipeline/etape'),
    staleTime: 60_000,
  })

  const schimba = useMutation({
    mutationFn: (v: { id: number; stage?: string | null; waiting_on?: string | null }) =>
      api.put(`/pipeline/rezervare/${v.id}`, { stage: v.stage, waiting_on: v.waiting_on }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rezervari'] })
      qc.invalidateQueries({ queryKey: ['pipeline-etape'] })
    },
  })
  const [q, setQ] = useState('')
  // Cand se intra direct pe o etapa (din tablou), filtrul „doar active" ar taia dosarele
  // incheiate si numarul din palnie n-ar mai corespunde cu lista (126 vs. 115, masurat).
  const [doarActive, setDoarActive] = useState(
    !statusUrl && !cauta.get('asteptam') && !cauta.get('etapa'))
  const [pagina, setPagina] = useState(0)
  const [pePagina, setPePagina] = useState(25)
  const [sort, setSort] = useState<Sortare>({ cheie: 'begin_date', sens: 'desc' })
  const [mod, setMod] = useState<ModCurs>(citesteMod)
  const [manual, setManual] = useState<number | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['rezervari', q, doarActive, statusUrl, etapaUrl, asteptamUrl, pagina, pePagina, sort],
    queryFn: () => api.get<Lista>('/reservations/list-fast', {
      search: q, active_only: doarActive, status: statusUrl, stage: etapaUrl,
      waiting_on: asteptamUrl,
      include_cancelled: statusUrl === 'Anulata',
      limit: pePagina, offset: pagina * pePagina,
      sort: sort.cheie, sort_dir: sort.sens,
    }),
    placeholderData: keepPreviousData,
  })

  const { data: curs } = useQuery({
    queryKey: ['curs-bnr'],
    queryFn: () => api.get<{ eur_rate: number }>('/invoices/page', { per_page: 1 }),
    staleTime: 30 * 60_000,
  })

  const ctx: ContextCurs = { bnrPlus2: curs?.eur_rate ?? 0, mod, manual }
  const rows = data?.reservations ?? []

  const coloane: Coloana<Rezervare>[] = [
    { cheie: 'number', antet: 'Comanda', sortabil: true, cheieServer: 'number',
      randare: (r) => <span className="tnum font-medium">{r.number}</span> },
    { cheie: 'partner', antet: 'Client', sortabil: true, cheieServer: 'partner',
      randare: (r) => <span className="block max-w-[200px] truncate">{r.partner ?? r.booking_customer ?? '—'}</span> },
    { cheie: 'service', antet: 'Serviciu',
      randare: (r) => (
        <span className="block max-w-[240px] truncate text-ink-soft">
          {r.service ?? '—'}
          {(r.main_destination || r.destination) && (
            <span className="text-muted"> · {r.main_destination ?? r.destination}</span>
          )}
        </span>
      ) },
    { cheie: 'begin_date', antet: 'Perioada', sortabil: true, cheieServer: 'begin_date',
      randare: (r) => (
        <span className="text-muted tnum text-[13px] whitespace-nowrap">{zi(r.begin_date)} – {zi(r.end_date)}</span>
      ) },
    { cheie: 'responsible', antet: 'Responsabil', sortabil: true, cheieServer: 'responsible',
      randare: (r) => <span className="block max-w-[140px] truncate text-[13px] text-muted">{r.responsible ?? '—'}</span> },
    { cheie: 'total_value', antet: 'Valoare', sortabil: true, cheieServer: 'total_value', aliniat: 'dreapta',
      randare: (r) => (r.total_value
        ? <Suma {...pereche(r.total_value, { currency: r.currency ?? 'RON', exchange_rate: null, invoice_type: 'Sales' }, ctx)}
            explicatie={sursaCurs({ currency: r.currency ?? 'RON', exchange_rate: null, invoice_type: 'Sales' }, ctx)} />
        : <span className="text-muted">—</span>) },
    { cheie: 'stage', antet: 'Etapa', sortabil: true, cheieServer: 'stage',
      randare: (r) => (
        <select value={r.stage ?? ''} onClick={(e) => e.stopPropagation()}
          onChange={(e) => schimba.mutate({ id: r.id, stage: e.target.value || null })}
          className={'h-8 px-2 rounded-full text-[12.5px] border-0 outline-none cursor-pointer ' +
            (CULOARE_ETAPA[r.stage ?? ''] ?? 'bg-page text-muted')}>
          <option value="">— fara etapa —</option>
          {(pipeline.data?.etape ?? []).map((e) => <option key={e.cod} value={e.cod}>{e.nume}</option>)}
        </select>
      ) },
    { cheie: 'waiting_on', antet: 'Asteptam dupa', sortabil: true, cheieServer: 'waiting_on',
      randare: (r) => (
        <select value={r.waiting_on ?? ''} onClick={(e) => e.stopPropagation()}
          onChange={(e) => schimba.mutate({ id: r.id, waiting_on: e.target.value || null })}
          className={'h-8 px-2 rounded-[var(--radius-control)] text-[12.5px] bg-transparent border border-line ' +
            (r.waiting_on === 'noi' ? 'text-bad border-bad/40' : 'text-muted')}>
          {ASTEPTAM.map((a) => <option key={a.cod} value={a.cod}>{a.nume}</option>)}
        </select>
      ) },
    { cheie: 'status', antet: 'Stare', sortabil: true, cheieServer: 'status', randare: (r) => stare(r.status) },
  ]

  return (
    <>
      <PageHeader
        title={statusUrl ? `Rezervari · ${statusUrl}`
          : asteptamUrl === 'noi' ? 'Rezervari · mingea la noi'
          : etapaUrl ? `Rezervari · ${(pipeline.data?.etape ?? []).find((e) => e.cod === etapaUrl)?.nume ?? etapaUrl}`
          : 'Rezervari'}
        count={data ? `${data.total.toLocaleString('ro-RO')} in total` : undefined}
        actions={<><CursSelector ctx={ctx} onMod={setMod} onManual={setManual} /><NewMenu /></>}
      />
      <div className="px-8 py-6 space-y-5">
        {!statusUrl && !asteptamUrl && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Pill active={doarActive && !etapaUrl} onClick={() => {
                setDoarActive(true); setPagina(0); setCauta({})
              }}>Active</Pill>
              <Pill active={!doarActive && !etapaUrl} onClick={() => {
                setDoarActive(false); setPagina(0); setCauta({})
              }}>Toate</Pill>
            </div>

            {/* Palnia comerciala: se apasa o etapa si lista se filtreaza pe ea. */}
            <div className="flex flex-wrap gap-1.5">
              {(pipeline.data?.etape ?? []).map((e) => (
                <button key={e.cod}
                  onClick={() => { setPagina(0); setDoarActive(false); setCauta(etapaUrl === e.cod ? {} : { etapa: e.cod }) }}
                  className={'px-3 h-9 rounded-[var(--radius-control)] text-[13px] border transition-colors ' +
                    (etapaUrl === e.cod ? 'border-accent bg-accent-soft text-accent-ink font-medium'
                                        : 'border-line text-muted hover:border-line-strong')}>
                  {e.nume}<span className="ml-1.5 tnum">{e.numar ?? 0}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <DataTable
          randuri={rows} coloane={coloane} cheie={(r) => r.id} incarca={isPending}
          peRand={(r) => navigheaza(`/rezervari/${r.id}`)}
          placeholderCautare="Numar, client, destinatie…"
          gol={{ titlu: 'Nicio rezervare', indiciu: 'Schimba filtrul sau cauta altceva.' }}
          server={{
            total: data?.total ?? 0, pagina, pePagina, setPagina, setPePagina,
            termen: q, setTermen: (v) => { setQ(v); setPagina(0) },
            sort, setSort,
          }}
        />
      </div>
    </>
  )
}
